import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FileText, Loader2, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/Common/button';
import { Badge } from '@/components/Common/badge';
import Modal from '@/components/Common/Modal';
import notify from '@/lib/notify';
import { BRANDED_PDF_ENABLED } from '@/lib/runtimeFlags';
import {
  documentTemplateService,
  DocumentTemplate,
  DocumentTemplateFormat,
  PlaceholderSpec,
  isFileTemplateFormat,
} from '@/services/documentTemplateService';

const OUTPUT_FORMATS: DocumentTemplateFormat[] = BRANDED_PDF_ENABLED
  ? ['md', 'docx', 'pdf', 'pptx']
  : ['md', 'docx', 'pptx'];

// A .docx source drives docx/pdf; pptx has its own source file.
const acceptForFormat = (fmt: DocumentTemplateFormat): string =>
  fmt === 'pptx' ? '.pptx' : '.docx';

interface TemplateFormState {
  name: string;
  description: string;
  instructions: string;
  output_format: DocumentTemplateFormat;
  sectionsText: string;
  placeholders: PlaceholderSpec[];
  is_active: boolean;
}

const EMPTY_FORM: TemplateFormState = {
  name: '',
  description: '',
  instructions: '',
  output_format: 'docx',
  sectionsText: '',
  placeholders: [],
  is_active: true,
};

const toFormState = (template: DocumentTemplate): TemplateFormState => ({
  name: template.name,
  description: template.description || '',
  instructions: template.instructions || '',
  output_format: template.output_format,
  sectionsText: (template.structure?.sections || []).join('\n'),
  placeholders: template.structure?.placeholders || [],
  is_active: template.is_active,
});

/**
 * Document-template management rendered as a table with a modal editor.
 * Lives in the organization "Templates" tab alongside interview templates.
 */
export function DocumentTemplatesSection() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [form, setForm] = useState<TemplateFormState>(EMPTY_FORM);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DocumentTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await documentTemplateService.list();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load document templates:', error);
      notify.error(
        t('knowledgeManagement.documentTemplates.loadError', 'Impossible de charger les modèles de documents')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreateForm = () => {
    setEditingTemplate(null);
    setForm(EMPTY_FORM);
    setTemplateFile(null);
    setFormOpen(true);
  };

  const openEditForm = (template: DocumentTemplate) => {
    setEditingTemplate(template);
    setForm(toFormState(template));
    setTemplateFile(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditingTemplate(null);
    setTemplateFile(null);
  };

  const handleFormatChange = (output_format: DocumentTemplateFormat) => {
    // Switching format resets a pending upload; a docx source is shared by
    // docx/pdf so its extracted placeholders remain valid across those two.
    setForm((prev) => ({ ...prev, output_format }));
    if (output_format === 'md') {
      setTemplateFile(null);
    }
  };

  const handleFileSelected = async (file: File | null) => {
    setTemplateFile(file);
    if (!file || !isFileTemplateFormat(form.output_format)) return;
    setExtracting(true);
    try {
      const names = await documentTemplateService.extractPlaceholders(form.output_format, file);
      // Carry over instructions already written for placeholders of the same name.
      const existing = new Map(form.placeholders.map((p) => [p.name, p.instruction]));
      setForm((prev) => ({
        ...prev,
        placeholders: names.map((name) => ({ name, instruction: existing.get(name) || '' })),
      }));
      if (names.length === 0) {
        notify.info(
          t(
            'knowledgeManagement.documentTemplates.noPlaceholders',
            'Aucun champ {{ ... }} détecté dans ce fichier.'
          )
        );
      }
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      notify.error(
        status === 501
          ? t(
              'knowledgeManagement.documentTemplates.extractUnavailable',
              "Le service de lecture des modèles n'est pas disponible."
            )
          : t(
              'knowledgeManagement.documentTemplates.extractError',
              'Impossible de lire les champs de ce fichier.'
            )
      );
      setTemplateFile(null);
    } finally {
      setExtracting(false);
    }
  };

  const updatePlaceholderInstruction = (name: string, instruction: string) => {
    setForm((prev) => ({
      ...prev,
      placeholders: prev.placeholders.map((p) => (p.name === name ? { ...p, instruction } : p)),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify.error(t('knowledgeManagement.documentTemplates.nameRequired', 'Le nom est requis'));
      return;
    }

    const fileMode = isFileTemplateFormat(form.output_format);
    const hasExistingFile = !!editingTemplate?.has_template_file;
    if (fileMode && !templateFile && !hasExistingFile) {
      notify.error(
        t(
          'knowledgeManagement.documentTemplates.fileRequired',
          'Veuillez téléverser un fichier modèle pour ce format.'
        )
      );
      return;
    }

    setSaving(true);
    try {
      let structure: Record<string, unknown> | null = null;
      if (fileMode) {
        structure = { placeholders: form.placeholders };
      } else {
        const sections = form.sectionsText
          .split('\n')
          .map((section) => section.trim())
          .filter(Boolean);
        structure = sections.length > 0 ? { sections } : null;
      }

      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        instructions: form.instructions.trim() || null,
        output_format: form.output_format,
        structure,
        is_active: form.is_active,
      };

      if (editingTemplate) {
        await documentTemplateService.update(editingTemplate.id, payload, templateFile);
        notify.success(t('knowledgeManagement.documentTemplates.updated', 'Modèle mis à jour'));
      } else {
        await documentTemplateService.create(payload, templateFile);
        notify.success(t('knowledgeManagement.documentTemplates.created', 'Modèle créé'));
      }

      setFormOpen(false);
      setEditingTemplate(null);
      setTemplateFile(null);
      await loadTemplates();
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      notify.error(
        status === 409
          ? t('knowledgeManagement.documentTemplates.duplicateName', 'Un modèle avec ce nom existe déjà.')
          : t('knowledgeManagement.documentTemplates.saveError', "Erreur lors de l'enregistrement du modèle")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await documentTemplateService.remove(deleteTarget.id);
      notify.success(t('knowledgeManagement.documentTemplates.deleted', 'Modèle supprimé'));
      setDeleteTarget(null);
      await loadTemplates();
    } catch (error) {
      console.error('Failed to delete document template:', error);
      notify.error(
        t('knowledgeManagement.documentTemplates.deleteError', 'Erreur lors de la suppression du modèle')
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-foreground">
            {t('organizationTemplates.documentsTitle', 'Modèles de documents')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t(
              'organizationTemplates.documentsDescription',
              'Modèles réutilisables (instructions, structure, format de sortie) pour la génération de documents depuis les conversations. Les agents peuvent être restreints à certains modèles.'
            )}
          </p>
        </div>
        <Button onClick={openCreateForm} size="sm" className="shrink-0">
          <Plus className="h-4 w-4 mr-2" />
          {t('organizationTemplates.newDocument', 'Nouveau modèle')}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2 py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('knowledgeManagement.documentTemplates.loading', 'Chargement des modèles...')}
        </p>
      ) : templates.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">
          {t('organizationTemplates.emptyDocuments', 'Aucun modèle de document pour le moment.')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm text-left text-muted-foreground">
            <thead className="text-xs uppercase bg-muted/50 text-muted-foreground">
              <tr>
                <th scope="col" className="px-4 py-3">
                  {t('organizationTemplates.columns.name', 'Nom')}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t('organizationTemplates.columns.format', 'Format')}
                </th>
                <th scope="col" className="px-4 py-3 text-center">
                  {t('organizationTemplates.columns.fields', 'Champs')}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t('organizationTemplates.columns.status', 'Statut')}
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  {t('organizationTemplates.columns.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{template.name}</div>
                    {template.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {template.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{template.output_format.toUpperCase()}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {template.structure?.placeholders?.length ??
                      template.structure?.sections?.length ??
                      0}
                  </td>
                  <td className="px-4 py-3">
                    {template.is_active ? (
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300 hover:bg-green-100">
                        {t('knowledgeManagement.documentTemplates.activeBadge', 'Actif')}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        {t('knowledgeManagement.documentTemplates.inactiveBadge', 'Inactif')}
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end items-center gap-2">
                      <Button onClick={() => openEditForm(template)} variant="outline" size="sm">
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        {t('knowledgeManagement.documentTemplates.edit', 'Modifier')}
                      </Button>
                      <Button
                        onClick={() => setDeleteTarget(template)}
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        {t('knowledgeManagement.documentTemplates.delete', 'Supprimer')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={formOpen}
        title={
          editingTemplate
            ? t('knowledgeManagement.documentTemplates.editTitle', 'Modifier le modèle')
            : t('knowledgeManagement.documentTemplates.createTitle', 'Nouveau modèle de document')
        }
        onClose={closeForm}
        className="max-w-2xl"
        bodyClassName="max-h-[70vh] overflow-y-auto"
        hideDefaultFooter
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium">
              {t('knowledgeManagement.documentTemplates.name', 'Nom')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
              placeholder={t(
                'knowledgeManagement.documentTemplates.namePlaceholder',
                'Compte-rendu de réunion'
              )}
              maxLength={255}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium">
              {t('knowledgeManagement.documentTemplates.description', 'Description')}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
              placeholder={t(
                'knowledgeManagement.documentTemplates.descriptionPlaceholder',
                'À quoi sert ce modèle ?'
              )}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium">
              {t('knowledgeManagement.documentTemplates.instructions', 'Instructions')}
            </label>
            <textarea
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm font-mono"
              placeholder={t(
                'knowledgeManagement.documentTemplates.instructionsPlaceholder',
                'Consignes de rédaction transmises à l’IA lors de la génération...'
              )}
              rows={5}
            />
            <p className="text-xs text-muted-foreground">
              {t(
                'knowledgeManagement.documentTemplates.instructionsHelp',
                'Ces consignes guident le contenu et le ton du document généré.'
              )}
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium">
              {t('knowledgeManagement.documentTemplates.outputFormat', 'Format de sortie')}
            </label>
            <select
              value={form.output_format}
              onChange={(e) => handleFormatChange(e.target.value as DocumentTemplateFormat)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
            >
              {(OUTPUT_FORMATS.includes(form.output_format) ? OUTPUT_FORMATS : [...OUTPUT_FORMATS, form.output_format]).map((format) => (
                <option key={format} value={format}>
                  {format.toUpperCase()}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              {isFileTemplateFormat(form.output_format)
                ? t(
                    'knowledgeManagement.documentTemplates.fileFormatHelp',
                    'Téléversez un document déjà mis en forme (charte, logos, couleurs) contenant des champs {{ ... }} ; l’IA remplira chaque champ.'
                  )
                : t(
                    'knowledgeManagement.documentTemplates.mdFormatHelp',
                    'L’IA rédige le contenu en Markdown à partir des sections et instructions.'
                  )}
            </p>
          </div>

          {isFileTemplateFormat(form.output_format) ? (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium">
                  {t('knowledgeManagement.documentTemplates.templateFile', 'Fichier modèle')}
                  {!editingTemplate?.has_template_file && <span className="text-red-500"> *</span>}
                </label>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-md bg-background text-sm cursor-pointer hover:bg-muted/40">
                    <Upload className="h-4 w-4" />
                    {form.output_format === 'pptx'
                      ? t('knowledgeManagement.documentTemplates.uploadPptx', 'Choisir un .pptx')
                      : t('knowledgeManagement.documentTemplates.uploadDocx', 'Choisir un .docx')}
                    <input
                      type="file"
                      accept={acceptForFormat(form.output_format)}
                      className="hidden"
                      onChange={(e) => void handleFileSelected(e.target.files?.[0] || null)}
                    />
                  </label>
                  {extracting && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  {templateFile ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
                      <FileText className="h-4 w-4" />
                      {templateFile.name}
                    </span>
                  ) : editingTemplate?.template_file_name ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      {editingTemplate.template_file_name}
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  {form.output_format === 'pdf'
                    ? t(
                        'knowledgeManagement.documentTemplates.pdfSourceHelp',
                        'Le PDF est généré à partir d’un modèle Word (.docx) mis en forme.'
                      )
                    : t(
                        'knowledgeManagement.documentTemplates.placeholderSyntaxHelp',
                        'Dans votre document, écrivez les champs sous la forme {{ nom_du_champ }}.'
                      )}
                </p>
              </div>

              {form.placeholders.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    {t(
                      'knowledgeManagement.documentTemplates.placeholders',
                      'Champs détectés — consignes de remplissage'
                    )}
                  </label>
                  <div className="space-y-2">
                    {form.placeholders.map((ph) => (
                      <div
                        key={ph.name}
                        className="rounded-md border border-border/60 p-3 space-y-1.5"
                      >
                        <code className="text-xs font-mono text-primary">{`{{ ${ph.name} }}`}</code>
                        <textarea
                          value={ph.instruction}
                          onChange={(e) => updatePlaceholderInstruction(ph.name, e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                          placeholder={t(
                            'knowledgeManagement.documentTemplates.placeholderInstructionPlaceholder',
                            'Comment remplir ce champ à partir de la conversation ?'
                          )}
                          rows={2}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                {t('knowledgeManagement.documentTemplates.sections', 'Sections (une par ligne)')}
              </label>
              <textarea
                value={form.sectionsText}
                onChange={(e) => setForm({ ...form, sectionsText: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm font-mono"
                placeholder={t(
                  'knowledgeManagement.documentTemplates.sectionsPlaceholder',
                  'Introduction\nSynthèse\nPlan d’action'
                )}
                rows={5}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  'knowledgeManagement.documentTemplates.sectionsHelp',
                  'Chaque ligne devient une section du document. Laissez vide pour une structure libre.'
                )}
              </p>
            </div>
          )}

          <label className="flex items-center gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:bg-muted/40">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="h-4 w-4 rounded border-border"
            />
            <span className="text-sm font-medium">
              {t('knowledgeManagement.documentTemplates.isActive', 'Modèle actif')}
            </span>
          </label>

          <div className="flex gap-2 justify-end pt-2 border-t border-border">
            <Button onClick={closeForm} variant="outline" disabled={saving}>
              {t('knowledgeManagement.documentTemplates.cancel', 'Annuler')}
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingTemplate
                ? t('knowledgeManagement.documentTemplates.save', 'Enregistrer')
                : t('knowledgeManagement.documentTemplates.create', 'Créer')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deleteTarget}
        title={t('knowledgeManagement.documentTemplates.deleteConfirmTitle', 'Supprimer le modèle')}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        className="max-w-lg"
        hideDefaultFooter
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground/90">
            {t('knowledgeManagement.documentTemplates.deleteConfirmMessage', {
              defaultValue:
                'Voulez-vous vraiment supprimer le modèle « {{name}} » ? Cette action est irréversible.',
              name: deleteTarget?.name ?? '',
            })}
          </p>
          <div className="flex gap-2 justify-end pt-2 border-t border-border">
            <Button onClick={() => setDeleteTarget(null)} variant="outline" disabled={deleting}>
              {t('knowledgeManagement.documentTemplates.cancel', 'Annuler')}
            </Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('knowledgeManagement.documentTemplates.delete', 'Supprimer')}
            </Button>
          </div>
        </div>
      </Modal>
    </section>
  );
}

export default DocumentTemplatesSection;
